import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Collapse, Typography } from "@mui/material";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import { useTranslation } from "react-i18next";
import codService from "../../../services/CodService";
import salesManagerEnrolmentService from "../../../services/SalesManagerEnrolmentService";
import { CodOrder, CodPaymentTransaction } from "../../../types/codSalesRep";
import { formatCodDate } from "../../../utils/codUtils";
import {
  buildInvoiceIndex,
  buildRepRows,
  InvoiceIndex,
  collectionRate,
  ReportDateRange,
  RepRef,
  scopeReportData,
  sumTotals,
} from "../../../utils/codReports";
import {
  buildInvoicesSheet,
  buildLinesSheet,
  buildOrdersSheet,
  buildPaymentsSheet,
  exportExcelWorkbook,
  reportFileStamp,
} from "../../../utils/codReportExcel";
import { showToast } from "../../component/common/toastNotification";
import CodSectionHeader from "../../../components/ui/CodSectionHeader";
import CodInitialsAvatar from "../../../components/ui/CodInitialsAvatar";
import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";
import {
  CollectionBar,
  MoneyStat,
  onActivate,
  ReportDetail,
  ReportKpis,
  reportRowHeaderSx,
  reportRowSx,
  ReportToolbar,
} from "../codSalesRep/CodReportParts";
import { NJIM } from "../codSalesRep/codStyles";
import CodTabBar from "../codSalesRep/CodTabBar";
import { ManagerChartsSection } from "../codSalesRep/CodReportCharts";

const CodManagerReports: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<CodOrder[]>([]);
  const [payments, setPayments] = useState<CodPaymentTransaction[]>([]);
  const [reps, setReps] = useState<RepRef[]>([]);
  const [paymentsFailed, setPaymentsFailed] = useState(false);
  // Sage invoices per order (COD money is tracked per invoice). Empty map = order-level fallback.
  const [invoiceIndex, setInvoiceIndex] = useState<InvoiceIndex>(() => new Map());
  const [range, setRange] = useState<ReportDateRange>({ from: "", to: "" });
  const [search, setSearch] = useState("");
  const [onlyBalance, setOnlyBalance] = useState(false);
  const [expandedRep, setExpandedRep] = useState("");
  const [expandedCustomer, setExpandedCustomer] = useState("");
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState<"list" | "charts">("list");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [orderRows, paymentRows, repRows] = await Promise.all([
        codService.listManagerOrders(),
        codService.listPaymentTransactions().then(
          (rows) => {
            setPaymentsFailed(false);
            return rows;
          },
          () => {
            setPaymentsFailed(true);
            return [] as CodPaymentTransaction[];
          }
        ),
        salesManagerEnrolmentService.getMyReps().catch(() => []),
      ]);
      setOrders(orderRows);
      // Invoices only for the team's approved orders (the endpoint would otherwise return every
      // customer for a manager). Chunked so request URLs stay short; chunks load in parallel.
      const approvedIds = orderRows
        .filter((order) => order.approvalStatus === 2 || order.approvalStatusName === "Approved")
        .map((order) => order.id);
      const chunks: string[][] = [];
      for (let i = 0; i < approvedIds.length; i += 60) chunks.push(approvedIds.slice(i, i + 60));
      const invoiceOrders = (
        await Promise.all(
          chunks.map((ids) =>
            codService.listInvoiceOrders({ orderIds: ids, includePaid: true }).catch(() => [])
          )
        )
      ).flat();
      setInvoiceIndex(buildInvoiceIndex(invoiceOrders));
      setPayments(paymentRows);
      setReps(
        repRows.map((rep) => ({
          repName: (rep.salesRepName || rep.salesRepCode).trim(),
          repCode: rep.salesRepCode,
        }))
      );
    } catch {
      showToast(t("codSalesManager.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const scoped = useMemo(() => scopeReportData(orders, payments, range), [orders, payments, range]);

  const allRows = useMemo(
    () => buildRepRows(scoped.orders, scoped.payments, orders, reps, t("codReports.unassigned"), invoiceIndex),
    [scoped, orders, reps, t, invoiceIndex]
  );

  /** Search matches a rep (name/code) or any of its customers; customer matches narrow the rep. */
  const visibleRows = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return allRows
      .map((rep) => {
        const repMatch =
          !keyword ||
          rep.repName.toLowerCase().includes(keyword) ||
          rep.repCode.toLowerCase().includes(keyword);
        const customers = rep.customers.filter((customer) => {
          if (onlyBalance && customer.remaining <= 0.005) return false;
          if (repMatch) return true;
          return (
            customer.customerName.toLowerCase().includes(keyword) ||
            customer.customerCode.toLowerCase().includes(keyword)
          );
        });
        return { rep, repMatch, customers };
      })
      .filter(({ rep, repMatch, customers }) => {
        if (onlyBalance && rep.remaining <= 0.005) return false;
        return repMatch || customers.length > 0;
      });
  }, [allRows, search, onlyBalance]);

  const totals = useMemo(() => sumTotals(visibleRows.map(({ rep }) => rep)), [visibleRows]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportOrders = visibleRows.flatMap(({ rep }) => rep.orderList);
      const exportPayments = visibleRows.flatMap(({ rep }) => rep.paymentList);

      const repSheetRows = visibleRows.map(({ rep }) => ({
        repName: rep.repName,
        repCode: rep.repCode,
        customers: rep.customers.length,
        orders: rep.orders,
        pending: rep.pending,
        approved: rep.approved,
        rejected: rep.rejected,
        approvedValue: rep.approvedValue,
        invoiced: rep.invoiced,
        notInvoiced: rep.notInvoiced,
        collected: rep.collected,
        remaining: rep.remaining,
        rate: collectionRate(rep),
        pendingValue: rep.pendingValue,
        payments: rep.payments,
        lastOrder: rep.lastOrder,
        lastPayment: rep.lastPayment,
      }));

      const repCustomerRows = visibleRows.flatMap(({ rep, customers }) =>
        customers.map((customer) => ({
          repName: rep.repName,
          repCode: rep.repCode,
          customerCode: customer.customerCode,
          customerName: customer.customerName,
          orders: customer.orders,
          pending: customer.pending,
          approved: customer.approved,
          rejected: customer.rejected,
          approvedValue: customer.approvedValue,
        invoiced: customer.invoiced,
        notInvoiced: customer.notInvoiced,
          collected: customer.collected,
          remaining: customer.remaining,
          rate: collectionRate(customer),
          payments: customer.payments,
          lastOrder: customer.lastOrder,
          lastPayment: customer.lastPayment,
        }))
      );

      const sumKey = (rows: Record<string, unknown>[], key: string) =>
        rows.reduce((acc, row) => acc + Number(row[key] ?? 0), 0);

      await exportExcelWorkbook(
        `COD-SalesRep-Report_${reportFileStamp()}`,
        [
          {
            name: t("codReports.sheetByRep"),
            columns: [
              { key: "repName", header: t("codReports.salesRep"), width: 30 },
              { key: "repCode", header: t("codReports.repCode"), width: 12 },
              { key: "customers", header: t("codReports.customers"), type: "int" },
              { key: "orders", header: t("codReports.orders"), type: "int" },
              { key: "pending", header: t("codSalesRep.pendingReview"), type: "int", width: 14 },
              { key: "approved", header: t("codSalesRep.approved"), type: "int" },
              { key: "rejected", header: t("codSalesRep.rejected"), type: "int" },
              { key: "approvedValue", header: t("codReports.approvedValue"), type: "money" },
              { key: "invoiced", header: t("codReports.invoiced"), type: "money" },
              { key: "notInvoiced", header: t("codReports.notInvoiced"), type: "money", width: 20 },
              { key: "collected", header: t("codReports.collected"), type: "money" },
              { key: "remaining", header: t("codReports.remaining"), type: "money" },
              { key: "rate", header: t("codReports.collectionRate"), type: "percent" },
              { key: "pendingValue", header: t("codReports.pendingValue"), type: "money", width: 20 },
              { key: "payments", header: t("codReports.payments"), type: "int" },
              { key: "lastOrder", header: t("codReports.lastOrder"), type: "date" },
              { key: "lastPayment", header: t("codReports.lastPayment"), type: "date" },
            ],
            rows: repSheetRows,
            totals: {
              repName: t("codReports.totalsRow"),
              customers: sumKey(repSheetRows, "customers"),
              orders: totals.orders,
              pending: totals.pending,
              approved: totals.approved,
              rejected: totals.rejected,
              approvedValue: totals.approvedValue,
              invoiced: totals.invoiced,
              notInvoiced: totals.notInvoiced,
              collected: totals.collected,
              remaining: totals.remaining,
              rate: collectionRate(totals),
              pendingValue: totals.pendingValue,
              payments: totals.payments,
            },
          },
          {
            name: t("codReports.sheetByRepCustomer"),
            columns: [
              { key: "repName", header: t("codReports.salesRep"), width: 30 },
              { key: "repCode", header: t("codReports.repCode"), width: 12 },
              { key: "customerCode", header: t("codReports.customerCode"), width: 18 },
              { key: "customerName", header: t("codReports.customerName"), width: 36 },
              { key: "orders", header: t("codReports.orders"), type: "int" },
              { key: "pending", header: t("codSalesRep.pendingReview"), type: "int", width: 14 },
              { key: "approved", header: t("codSalesRep.approved"), type: "int" },
              { key: "rejected", header: t("codSalesRep.rejected"), type: "int" },
              { key: "approvedValue", header: t("codReports.approvedValue"), type: "money" },
              { key: "invoiced", header: t("codReports.invoiced"), type: "money" },
              { key: "notInvoiced", header: t("codReports.notInvoiced"), type: "money", width: 20 },
              { key: "collected", header: t("codReports.collected"), type: "money" },
              { key: "remaining", header: t("codReports.remaining"), type: "money" },
              { key: "rate", header: t("codReports.collectionRate"), type: "percent" },
              { key: "payments", header: t("codReports.payments"), type: "int" },
              { key: "lastOrder", header: t("codReports.lastOrder"), type: "date" },
              { key: "lastPayment", header: t("codReports.lastPayment"), type: "date" },
            ],
            rows: repCustomerRows,
            totals: {
              repName: t("codReports.totalsRow"),
              orders: sumKey(repCustomerRows, "orders"),
              approvedValue: sumKey(repCustomerRows, "approvedValue"),
              invoiced: sumKey(repCustomerRows, "invoiced"),
              notInvoiced: sumKey(repCustomerRows, "notInvoiced"),
              collected: sumKey(repCustomerRows, "collected"),
              remaining: sumKey(repCustomerRows, "remaining"),
              payments: sumKey(repCustomerRows, "payments"),
            },
          },
          buildOrdersSheet(t, exportOrders, { includeRep: true, invoiceIndex }),
          buildInvoicesSheet(t, exportOrders, invoiceIndex, { includeRep: true }),
          buildLinesSheet(t, exportOrders, { includeRep: true }),
          buildPaymentsSheet(t, exportPayments, orders, { includeRep: true }),
        ],
        { rtl: i18n.language?.startsWith("ar") }
      );
      showToast(t("codReports.exportSuccess"), "success");
    } catch {
      showToast(t("codReports.exportFailed"), "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ borderRadius: "16px", border: `1px solid ${NJIM.border}`, background: NJIM.white, boxShadow: NJIM.elevation, p: { xs: 2, md: 3 } }}>
      <CodSectionHeader
        title={t("codReports.managerTitle")}
        helper={t("codReports.managerSubtitle")}
        action={<AssessmentRoundedIcon sx={{ color: NJIM.mint }} />}
      />

      <ReportToolbar
        range={range}
        onRangeChange={setRange}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t("codReports.searchRepOrCustomer")}
        onlyBalance={onlyBalance}
        onOnlyBalanceChange={setOnlyBalance}
        onExport={handleExport}
        exporting={exporting}
        exportDisabled={loading || visibleRows.length === 0}
      />

      {paymentsFailed && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {t("codReports.paymentsLoadFailed")}
        </Alert>
      )}

      <ReportKpis
        totals={totals}
        countLabel={t("codReports.reps")}
        count={visibleRows.length}
        countIcon={<BadgeRoundedIcon />}
        loading={loading}
      />

      <CodTabBar
        value={view}
        onChange={setView}
        tabs={[
          { value: "list" as const, label: t("codReports.listTab"), icon: <ViewListRoundedIcon sx={{ fontSize: 18 }} /> },
          { value: "charts" as const, label: t("codReports.chartsTab"), icon: <InsertChartRoundedIcon sx={{ fontSize: 18 }} /> },
        ]}
      />

      {view === "charts" && !loading ? (
        <ManagerChartsSection
          reps={visibleRows.map(({ rep }) => rep)}
          orders={visibleRows.flatMap(({ rep }) => rep.orderList)}
          payments={visibleRows.flatMap(({ rep }) => rep.paymentList)}
          totals={totals}
        />
      ) : loading ? (
        <TableSkeleton rows={6} columns={5} />
      ) : visibleRows.length === 0 ? (
        <EmptyState title={t("codReports.noData")} description={t("codReports.noDataHint")} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {visibleRows.map(({ rep, customers }) => {
            const isOpen = expandedRep === rep.key || (!!search.trim() && visibleRows.length === 1);
            const toggle = () => {
              setExpandedRep(isOpen ? "" : rep.key);
              setExpandedCustomer("");
            };
            return (
              <Box key={rep.key} sx={reportRowSx(isOpen)}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={toggle}
                  onKeyDown={onActivate(toggle)}
                  sx={{
                    ...reportRowHeaderSx,
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr) auto",
                      md: "minmax(0, 1.6fr) repeat(3, minmax(0, 1fr)) minmax(0, 0.7fr) auto",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <CodInitialsAvatar name={rep.repName} size={42} selected={isOpen} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {rep.repName}
                        {rep.repCode ? (
                          <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: NJIM.textMuted, marginInlineStart: "8px" }}>
                            {rep.repCode}
                          </Box>
                        ) : null}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {t("codReports.customers")}: {rep.customers.length} · {t("codReports.orders")}: {rep.orders}
                        {rep.pending > 0 ? ` (${t("codSalesRep.pendingReview")}: ${rep.pending})` : ""}
                        {` · ${t("codReports.payments")}: ${rep.payments}`}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <MoneyStat label={t("codReports.invoiced")} value={rep.invoiced} />
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <MoneyStat label={t("codReports.collected")} value={rep.collected} color={NJIM.success} />
                  </Box>
                  <MoneyStat
                    label={t("codReports.remaining")}
                    value={rep.remaining}
                    color={rep.remaining > 0.005 ? NJIM.danger : NJIM.textMuted}
                  />
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <CollectionBar totals={rep} />
                  </Box>
                  <KeyboardArrowDownRoundedIcon
                    sx={{
                      display: { xs: "none", md: "block" },
                      color: NJIM.textSecondary,
                      transform: isOpen ? "rotate(180deg)" : "none",
                      transition: `transform 180ms ${NJIM.easeOut}`,
                    }}
                  />
                </Box>

                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ p: { xs: 1.5, md: 2 }, borderTop: `1px solid ${NJIM.border}`, bgcolor: NJIM.muted }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: NJIM.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em", mb: 1.25 }}>
                      {t("codReports.customersUnderRep", { count: customers.length })}
                    </Typography>
                    {customers.length === 0 ? (
                      <Typography sx={{ fontSize: 13, color: NJIM.textMuted }}>{t("codReports.noOrders")}</Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {customers.map((customer) => {
                          const key = `${rep.key}|${customer.key}`;
                          const customerOpen = expandedCustomer === key;
                          const toggleCustomer = () => setExpandedCustomer(customerOpen ? "" : key);
                          return (
                            <Box key={key} sx={reportRowSx(customerOpen)}>
                              <Box
                                role="button"
                                tabIndex={0}
                                aria-expanded={customerOpen}
                                onClick={toggleCustomer}
                                onKeyDown={onActivate(toggleCustomer)}
                                sx={{
                                  ...reportRowHeaderSx,
                                  p: { xs: 1.25, md: 1.5 },
                                  gridTemplateColumns: {
                                    xs: "minmax(0, 1fr) auto",
                                    md: "minmax(0, 1.6fr) repeat(3, minmax(0, 1fr)) auto",
                                  },
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                                  <CodInitialsAvatar name={customer.customerName || customer.customerCode} size={32} />
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: NJIM.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {customer.customerName || customer.customerCode}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {customer.customerCode} · {t("codReports.orders")}: {customer.orders}
                                      {customer.lastOrder ? ` · ${t("codReports.lastOrder")} ${formatCodDate(customer.lastOrder)}` : ""}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: { xs: "none", md: "block" } }}>
                                  <MoneyStat label={t("codReports.invoiced")} value={customer.invoiced} />
                                </Box>
                                <Box sx={{ display: { xs: "none", md: "block" } }}>
                                  <MoneyStat label={t("codReports.collected")} value={customer.collected} color={NJIM.success} />
                                </Box>
                                <MoneyStat
                                  label={t("codReports.remaining")}
                                  value={customer.remaining}
                                  color={customer.remaining > 0.005 ? NJIM.danger : NJIM.textMuted}
                                />
                                <KeyboardArrowDownRoundedIcon
                                  sx={{
                                    display: { xs: "none", md: "block" },
                                    fontSize: 20,
                                    color: NJIM.textSecondary,
                                    transform: customerOpen ? "rotate(180deg)" : "none",
                                    transition: `transform 180ms ${NJIM.easeOut}`,
                                  }}
                                />
                              </Box>
                              <Collapse in={customerOpen} unmountOnExit>
                                <Box sx={{ p: { xs: 1.25, md: 1.5 }, borderTop: `1px solid ${NJIM.border}` }}>
                                  <ReportDetail orders={customer.orderList} payments={customer.paymentList} allOrders={orders} invoiceIndex={invoiceIndex} />
                                </Box>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default CodManagerReports;
